'use client';

import { c as _c } from "react/compiler-runtime";
import { useIsAuthenticated } from '@achra/sdk/react';
import { Alert, AlertDescription, AlertTitle } from '@achra/ui/alert';
import { Button } from '@achra/ui/button';
import { Card, CardContent } from '@achra/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@achra/ui/form';
import { Input } from '@achra/ui/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Send } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSubmitPurchaseRequest } from '@/modules/service-purchase/hooks/use-submit-purchase-request';
import { clearServicePurchasePersistedState } from '@/modules/service-purchase/lib/persistence-utils';
import { formDefaultValues, submitRequestSchema } from '@/modules/service-purchase/lib/submit-request-schema';
import { useServiceOffering, useServicePurchaseActions, useServicePurchaseState, useServicePurchaseStep } from '@/modules/service-purchase/providers/service-purchase-store-provider';
import { ServicePurchaseStep, type SubmitRequestFormValues } from '@/modules/service-purchase/types';
interface RequestFormProps {
  defaultName?: string;
  defaultTeamName?: string;
}
function RequestForm(t0) {
  const $ = _c(46);
  const {
    defaultName: t1,
    defaultTeamName: t2
  } = t0;
  const defaultName = t1 === undefined ? "" : t1;
  const defaultTeamName = t2 === undefined ? "" : t2;
  const {
    setRequestEntityData
  } = useServicePurchaseActions();
  const service = useServiceOffering();
  const {
    selectedTier,
    selectedBillingCycle,
    optionGroups
  } = useServicePurchaseState();
  const {
    goToStep
  } = useServicePurchaseStep();
  const isAuthenticated = useIsAuthenticated();
  const {
    mutate,
    isPending,
    error
  } = useSubmitPurchaseRequest();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      resolver: zodResolver(submitRequestSchema),
      mode: "onChange",
      defaultValues: formDefaultValues
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const form = useForm(t3);
  const {
    reset
  } = form;
  let t4;
  let t5;
  if ($[1] !== defaultName || $[2] !== defaultTeamName || $[3] !== reset) {
    t4 = () => {
      reset({
        name: defaultName,
        teamName: defaultTeamName,
        email: ""
      }, {
        keepDirtyValues: true
      });
    };
    t5 = [reset, defaultName, defaultTeamName];
    $[1] = defaultName;
    $[2] = defaultTeamName;
    $[3] = reset;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== goToStep || $[7] !== mutate || $[8] !== optionGroups || $[9] !== selectedBillingCycle || $[10] !== selectedTier || $[11] !== service || $[12] !== setRequestEntityData) {
    t6 = function onSubmit(data) {
      mutate({
        name: data.name,
        teamName: data.teamName,
        email: data.email,
        service,
        selectedTierId: selectedTier.id,
        selectedBillingCycle,
        optionGroups
      }, {
        onSuccess: result => {
          setRequestEntityData(result);
          goToStep(ServicePurchaseStep.Confirmation);
          clearServicePurchasePersistedState(service.id);
        }
      });
    };
    $[6] = goToStep;
    $[7] = mutate;
    $[8] = optionGroups;
    $[9] = selectedBillingCycle;
    $[10] = selectedTier;
    $[11] = service;
    $[12] = setRequestEntityData;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const onSubmit = t6;
  let t7;
  if ($[14] !== form || $[15] !== onSubmit) {
    t7 = event => {
      event.preventDefault();
      form.handleSubmit(onSubmit)(event);
    };
    $[14] = form;
    $[15] = onSubmit;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== error) {
    t8 = error && <Alert variant="destructive" role="alert"><AlertTitle>Error</AlertTitle><AlertDescription>{error.message}</AlertDescription></Alert>;
    $[17] = error;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== form.control) {
    t9 = <FormField control={form.control} name="name" render={_temp} />;
    $[19] = form.control;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = defaultTeamName === "" ? undefined : "hidden";
  let t11;
  if ($[21] !== form.control) {
    t11 = <FormField control={form.control} name="teamName" render={_temp2} />;
    $[21] = form.control;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <div className={t10}>{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== form.control) {
    t13 = <FormField control={form.control} name="email" render={_temp3} />;
    $[26] = form.control;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== isPending || $[29] !== t12 || $[30] !== t13 || $[31] !== t8 || $[32] !== t9) {
    t14 = <fieldset className="flex flex-col gap-4" disabled={isPending}>{t8}{t9}{t12}{t13}</fieldset>;
    $[28] = isPending;
    $[29] = t12;
    $[30] = t13;
    $[31] = t8;
    $[32] = t9;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = isPending || !isAuthenticated;
  let t16;
  if ($[34] !== isPending) {
    t16 = isPending ? <><Loader2 className="size-4 animate-spin" aria-hidden="true" />Submitting...</> : <>Request a Quote<Send className="size-4" aria-hidden="true" /></>;
    $[34] = isPending;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <div className="flex flex-col gap-4"><Button type="submit" className="w-full" disabled={t15}>{t16}</Button></div>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t14 || $[40] !== t17 || $[41] !== t7) {
    t18 = <form noValidate={true} onSubmit={t7} className="flex flex-col gap-6">{t14}{t17}</form>;
    $[39] = t14;
    $[40] = t17;
    $[41] = t7;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== form || $[44] !== t18) {
    t19 = <Card className="mx-auto w-full max-w-218.5 border-none py-0! lg:mx-0 lg:max-w-none"><CardContent className="p-3 lg:p-6"><Form {...form}>{t18}</Form></CardContent></Card>;
    $[43] = form;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  return t19;
}
function _temp3(t0) {
  const {
    field: field_1
  } = t0;
  return <FormItem><FormLabel htmlFor="submit-request-email" className="text-sm/3.5 font-medium">Email <span aria-hidden="true">*</span><span className="sr-only">(required)</span></FormLabel><FormControl><Input {...field_1} id="submit-request-email" type="email" placeholder="your@email.com" autoComplete="email" /></FormControl><p className="text-foreground/50 text-sm/5 font-normal">We'll send a PDF summary and next steps to this address.</p><FormMessage /></FormItem>;
}
function _temp2(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormLabel htmlFor="submit-request-team-name" className="text-sm/3.5 font-medium">Team Name <span aria-hidden="true">*</span><span className="sr-only">(required)</span></FormLabel><FormControl><Input {...field_0} id="submit-request-team-name" placeholder="Your team name" autoComplete="organization" /></FormControl><FormMessage /></FormItem>;
}
function _temp(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel htmlFor="submit-request-name" className="text-sm/3.5 font-medium">Name</FormLabel><FormControl><Input {...field} id="submit-request-name" placeholder="Your name" autoComplete="name" /></FormControl><FormMessage /></FormItem>;
}
export { RequestForm };